import { clsx } from "clsx";
import type { ReactElement, ReactNode } from "react";
import { forwardRef, useCallback, useState } from "react";
import type { AriaTabListProps } from "react-aria";

import type { ActionButtonProps, CardProps, PanelTab } from "widgets";
import {
  ActionButton,
  ActionButtonIntent,
  Callout,
  CalloutIntent,
  Card,
  TabListBar,
  TabPanel,
  TriggerButton,
  useTabList,
} from "widgets";

import style from "./styles/Dialogs.module.scss";

import PageHeader, { type IPageHeaderProps, cleanActions, zIntent } from "../navigation-panel/PageHeader.js";

export interface IPopoverProps {
  headerProps?: Omit<IPageHeaderProps, "newStyle" | "zIntent">;
  noHeaderActionWidgets?: boolean;
  closeFn?: () => void;
  mainActionButtonProps?: ActionButtonProps;
  secondaryActionButtonProps?: ActionButtonProps;
  noButtons?: boolean;
  footerMessage?: IDialogMessage;
  fullScreen?: boolean;
  noCardPadding?: boolean;
  children?: ReactNode;
  width?: number;
  overWriteMaxHeight?: boolean;
}

export interface IDialogMessage {
  message?: ReactNode;
  intent?: CalloutIntent;
}

function isPromise(value) {
  return Boolean(value && typeof value.then === "function");
}

/** @deprecated use from  widgets instead */
const EmptyDialog = forwardRef<HTMLDivElement, Omit<IPopoverProps, "fullScreen" | "noCardPadding">>(
  function Dialog(props, ref) {
    let actions: JSX.Element[];
    let visibleActions: JSX.Element[];

    if (props.noHeaderActionWidgets !== true && props.headerProps?.actionWidgets) {
      actions = cleanActions(props.headerProps.actionWidgets);
    }

    if (props.headerProps?.alwaysVisibleActionWidgets) {
      visibleActions = cleanActions(props.headerProps.alwaysVisibleActionWidgets);
    }

    if (typeof props.closeFn === "function") {
      if (visibleActions == undefined) visibleActions = [];

      visibleActions.push(
        <TriggerButton
          icon={"close"}
          onPress={() => props.closeFn()}
          key={"dialog-close"}
          label={""}
          aria-label={"Close"}
        />,
      );
    }

    // take care of some of the asynchronous boilerplate (is loading flag and error message caught)
    const [isAsyncLoading, setAsyncLoading] = useState<boolean>(false);
    const [asyncErrorMessage, setAsyncErrorMessage] = useState<string>();
    const handlePress = useCallback(() => {
      const onPressResult = props.mainActionButtonProps?.onPress();
      if (isPromise(onPressResult)) {
        setAsyncErrorMessage(null);
        setAsyncLoading(true);
        (onPressResult as Promise<any>)
          .then(() => {
            setAsyncLoading(false);
          })
          .catch((error: Error) => {
            setAsyncLoading(false);
            setAsyncErrorMessage(error.message);
          });
      }
    }, [props.mainActionButtonProps?.onPress]);

    const withHead = props.headerProps != undefined;

    return (
      <div
        className={clsx(style.popover, {
          [style.no_buttons]: props.noButtons,
          [style.overwriteMaxHeight]: props.overWriteMaxHeight,
        })}
        style={props.width ? { minWidth: "unset", width: props.width } : undefined}
        ref={ref}
      >
        {withHead && (
          <div className={style.header}>
            <PageHeader
              newStyle
              {...props.headerProps}
              actionWidgets={actions}
              alwaysVisibleActionWidgets={visibleActions}
              zIntent={zIntent.Popover}
            />
          </div>
        )}
        {props.children}
        {(asyncErrorMessage || props.footerMessage?.message) && (
          <Callout intent={props.footerMessage?.intent ?? CalloutIntent.Danger} className={style.flexible_callout}>
            {props.footerMessage?.message || asyncErrorMessage}
          </Callout>
        )}
        {props.noButtons !== true && (
          <div className={style.buttons}>
            <div>
              {props.dontUseSecondAction}
              {props.secondaryActionButtonProps && (
                <ActionButton
                  {...props.secondaryActionButtonProps}
                  intent={props.secondaryActionButtonProps.intent ?? ActionButtonIntent.Secondary}
                />
              )}
            </div>
            <div className={style.button_group}>
              {typeof props.closeFn === "function" && (
                <ActionButton intent={ActionButtonIntent.Secondary} onPress={() => props.closeFn()}>
                  Cancel
                </ActionButton>
              )}
              {props.mainActionButtonProps && (
                <ActionButton
                  {...props.mainActionButtonProps}
                  isLoading={isAsyncLoading || props.mainActionButtonProps.isLoading}
                  onPress={handlePress}
                  intent={props.mainActionButtonProps.intent ?? ActionButtonIntent.Primary}
                />
              )}
            </div>
          </div>
        )}
      </div>
    );
  },
);

/** @deprecated use from  widgets instead */
const Dialog = forwardRef<HTMLDivElement, IPopoverProps>(function Dialog(props, ref) {
  return (
    <EmptyDialog {...props} ref={ref}>
      {props.children && (
        <div
          className={clsx(style.card, {
            [style.card_fullscreen]: props.fullScreen,
            [style.no_card_padding]: props.noCardPadding,
          })}
        >
          {props.children}
        </div>
      )}
    </EmptyDialog>
  );
});

/** @deprecated use AlertDialog instead */
const ConfirmationDialog = forwardRef<HTMLDivElement, Omit<IPopoverProps, "noButtons" | "secondaryActionButtonProps">>(
  function ConfirmationDialog(props, ref) {
    return (
      <EmptyDialog {...props} ref={ref} noHeaderActionWidgets={true}>
        {props.children && (
          <div
            className={clsx(style.card, {
              [style.card_fullscreen]: props.fullScreen,
              [style.no_card_padding]: props.noCardPadding,
            })}
          >
            {props.children}
          </div>
        )}
      </EmptyDialog>
    );
  },
);

interface TabDialogProps extends Omit<IPopoverProps, "children">, AriaTabListProps<ReactElement> {
  /** ! Use only when needed. It may significantly decrease performance */
  keepAlive?: boolean;
  empty?: boolean;
}

interface TabDialogItemProps {
  cardProps: CardProps;
}

/** @deprecated */
const TabDialog = forwardRef<HTMLDivElement, Omit<TabDialogProps, "noCardPadding">>(function TabDialog(
  { children, defaultSelectedKey, keepAlive, empty, ...props },
  ref,
) {
  const { ref: tabRef, state, tabListProps } = useTabList<HTMLDivElement>({ children, defaultSelectedKey });

  const createInfoCard = ({ children, key, itemProps }: PanelTab<TabDialogItemProps>) => {
    return empty ? (
      <div
        className={clsx({
          [style.card_fullscreen]: props.fullScreen,
          [style.card_fullscreen_with_tab]: props.fullScreen,
        })}
        key={key}
      >
        {children}
      </div>
    ) : (
      <Card
        {...(itemProps?.cardProps ?? {})}
        title={itemProps?.cardProps?.title}
        scrollable={itemProps?.cardProps?.scrollable ?? true}
        className={clsx(itemProps?.cardProps?.className, { [style.card_fullscreen]: props.fullScreen })}
        key={key}
      >
        {children}
      </Card>
    );
  };
  //[empty, state, tabListProps],
  return (
    <EmptyDialog {...props} ref={ref}>
      {children && (
        <>
          <TabListBar state={state} tabListProps={tabListProps} ref={tabRef} style={{ margin: 0 }} />
          {keepAlive ? (
            <TabPanel<TabDialogItemProps>
              state={state}
              keepAlive
              className={style.tab_container}
              childrenClassName={style.tab_container}
            >
              {(panelTabs: Array<PanelTab<TabDialogItemProps>>) => panelTabs.map(createInfoCard)}
            </TabPanel>
          ) : (
            <TabPanel<TabDialogItemProps> state={state} className={style.tab_container} key={state.selectedKey}>
              {createInfoCard}
            </TabPanel>
          )}
        </>
      )}
    </EmptyDialog>
  );
});

export { ConfirmationDialog, Dialog, EmptyDialog, TabDialog };
